<template>
    <div class="sc__analysis__tab__box tab-content">
        <!-- Header -->
        <div class="sc__analysis__head__box">
            <div class="title__box">
                <h3 class="title">Target Guide</h3>
                <div class="print__data__btn">
                    <WorkpacketPdf :button-label="`Download`" :assessmentId="assessmentId"
                        :organizationId="organizationId" :learnerId="learnerId"
                        v-if="assessmentId && organizationId && learnerId" />
                </div>
            </div>
            <div class="head__label__box__wrapper">
                <div class="lable____head__title">
                    <label class="label__title">Directions</label>
                    <label class="label__title">Info Source</label>
                </div>
                <div class="divider__sepline"></div>
                <div class="head__info__box">
                    <div class="info__box">
                        <p>Performance range based on items scored 1 is highlighted.</p>
                    </div>
                    <div class="info__box analysis_grp1item analysis_grpitem active">
                        <p>Group {{ group }} SC Analysis</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- SC Analysis Sections -->
        <div class="sc__analysis__data__box">
            <div class="tabs__data__wrapper target__guide__data target_grp1item target_grpitem active">
                <div class="data__title__box">
                    <span>How Many Items Scored 1 Point?</span>
                    <span>frequent number for 1-Point items</span>
                    <span>General Tips</span>
                </div>
                <div class="target__guide__data__box">
                    <template v-for="range in scoreRanges" :key="range.label">
                        <div class="target__data__group__wrapper"
                            :class="{ active__target__group: range.condition(groupAttemptsScoreOneSums) }">
                            <div class="data__item__box scored__data">
                                <label>{{ range.label }}</label>
                                <span>{{ range.range }}</span>
                            </div>
                            <div class="data__item__box frequent__number__data">
                                <div class="frequent__number__box" v-for="(value, key) in scvAverages" :key="key">
                                    <label>{{ key === 'syllable' ? 'Syll' : key.charAt(0).toUpperCase() }}</label>
                                    <span>{{ range.condition(groupAttemptsScoreOneSums) ? value : '-' }}</span>
                                </div>
                            </div>
                            <div class="data__item__box general__tips__note">
                                <div class="tips__text__wrapper">
                                    <p>{{ range.tip }}</p>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <!-- Error Analysis Sections -->
        <div class="error__analysis__widget__box">
            <div v-for="n in 3" :key="'grp' + n" class="analysis__data__group"
                :class="['target_grp' + n + 'item', 'target_grpitem', { active: n === 1 }]"
                :style="{ display: n === 1 ? '' : 'none' }">
                <div class="error__analysis__content__box">
                    <h3 class="title">Error analysis - which skills are a priority?</h3>
                    <div class="error__description__wrapper">
                        <p>
                            How many items scored 0 points?<br />
                            info from Group {{ group }} test page
                            <br /><br /><br />
                            In Group {{ group }}, a score of “0” indicates inaccurate vowel(s) OR all consonants were
                            omitted.
                            This means syllable complexity should straddle Group {{ group }} - Group {{ group + 1 }}
                            skills if
                            there are a lot
                            of “0”
                            scores.
                        </p>
                    </div>
                </div>
                <div class="error__analysis__feature__box">
                    <template v-for="(range, index) in errorAnalysisTips" :key="range.points">
                        <div class="error__feature__box"
                            :class="{ active__error__box: range.condition(groupAttemptsScoreZeroSums) }">
                            <div class="feature__average__points">
                                <h5 class="points__text">{{ range.points }}</h5>
                                <span v-if="index === 0" class="text">High error</span>
                            </div>
                            <div class="feature__error__textarea">
                                <p>{{ range.tip }}</p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';
import WorkpacketPdf from './WorkpacketPdf.vue';

const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    learnerId: Number,
    group: Number,
});

const groupAttemptsScoreOneSum = ref(0);
const groupAttemptsScoreZeroSum = ref(0);
const scvAverages = ref({ syllable: '-', consonant: '-', vowel: '-' });

const loader = useLoaderStore();
const scoreRanges = [
    {
        label: 'Low Accurary',
        range: '(0-9)',
        condition: (sum) => sum <= 9,
        tip: 'Strengthen skills at this level before increasing SC',
    },
    {
        label: 'Low Accurary',
        range: '(10-19)',
        condition: (sum) => sum >= 10 && sum <= 19,
        tip: 'OK to toggle * this SC level with next higher level',
    },
    {
        label: 'Low Accurary',
        range: '(20-25)',
        condition: (sum) => sum >= 20,
        tip: 'Go to next higher SC level',
    },
];

const errorAnalysisTips = [
    {
        points: '20-25',
        tip: 'Target vowels and diphthongs in 2-syllable strings. Achieve accuracy on these before increasing syllable complexity.',
        condition: (sum) => sum >= 20,
    },
    {
        points: '10-19',
        tip: 'Target vowels/diphthongs in 2-syllable strings. Monitor for consonant deletions. Alternate target syllable complexity between achieved and need-to-achieve for Group 2.',
        condition: (sum) => sum >= 10 && sum <= 19,
    },
    {
        points: '0-9',
        tip: 'Monitor vowel accuracy and consonant inclusion in 2-3 syllable strings at SC code levels in Work Packet, Group 2 SC Analysis. See above if accuracy is not progressing.',
        condition: (sum) => sum <= 9,
    },
];

const groupAttemptsScoreOneSums = computed(() => {
    return groupAttemptsScoreOneSum.value
})

const groupAttemptsScoreZeroSums = computed(() => {
    return groupAttemptsScoreZeroSum.value
})

const fetchAssessment = async () => {
    loader.show();
    try {
        const { data } = await api.get(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}`,
            { params: { group: props.group } }
        );

        const groupScoreData = data?.data?.result?.group_scores?.[props.group];
        const scvAverageData = data?.data?.result?.scv_averages?.[props.group];

        if (groupScoreData) {
            groupAttemptsScoreOneSum.value = groupScoreData.score_one ?? 0;
            groupAttemptsScoreZeroSum.value = groupScoreData.score_zero ?? 0;
        } else {
            console.warn(`Missing group_scores for group ${props.group}`);
        }

        if (scvAverageData) {
            scvAverages.value = scvAverageData;
        } else {
            scvAverages.value = { syllable: '-', consonant: '-', vowel: '-' };
        }

    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

watch(() => props.group, fetchAssessment);
onMounted(fetchAssessment);
</script>